'use client';

import replacePlytixAssetCdnUrl from '@app/lib/bunny-cdn/replace-plytix-asset-cdn-url';
import replaceShopifyCdnUrl from '@app/lib/bunny-cdn/replace-shopify-cdn-url';
import cn from 'clsx';
import { StaticImage } from 'mmds';
import { FreeMode, Mousewheel, Scrollbar } from 'swiper/modules';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';
import s from './Swiper.module.css';
import { SwiperItem, SwiperProps, VideoItem } from './type';

interface VideoSlideContentProps {
    video: VideoItem;
    poster?: string;
    aspectRatio: string;
}

const VideoSlideContent = ({
    video,
    poster,
    aspectRatio,
}: VideoSlideContentProps) => {
    const swiper = useSwiper();

    return (
        <div
            style={{
                aspectRatio,
                width: '100%',
                overflow: 'hidden',
                position: 'relative',
            }}
        >
            <video
                autoPlay={video.autoplay}
                muted={video.muted}
                playsInline
                loop={video.loop}
                controls={video.controls}
                poster={replacePlytixAssetCdnUrl(poster)}
                preload="auto"
                onLoadedMetadata={() => swiper.update()}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                }}
            >
                {video.mobileVideoUrl && (
                    <source
                        media="(max-width: 991px)"
                        src={replacePlytixAssetCdnUrl(video.mobileVideoUrl)}
                        type="video/mp4"
                    />
                )}
                <source
                    src={replacePlytixAssetCdnUrl(
                        video.desktopVideoUrl || video.mobileVideoUrl
                    )}
                    type="video/mp4"
                />
            </video>
        </div>
    );
};

interface RenderSwiperProps {
    direction: 'vertical' | 'horizontal';
    cssMode: boolean;
    freeMode?: boolean;
    items: SwiperItem[];
    fallbackImageAltText?: string;
    videoAspectRatio: string;
}

const RenderSwiper = ({
    direction,
    cssMode,
    freeMode,
    items,
    fallbackImageAltText,
    videoAspectRatio,
}: RenderSwiperProps) => {
    return (
        <>
            <Swiper
                direction={direction}
                modules={[Scrollbar, Mousewheel, FreeMode]}
                className={cn(s.pdpGallery, 'swiper-pdp-gallery')}
                scrollbar={true}
                mousewheel={{
                    forceToAxis: false,
                    releaseOnEdges: true,
                    // On macbook trackpad, a swipe gesture was often causing a scroll of two slides (i.e. skipping a slide).
                    // Adjusting the threshold delta and threshold time fixes that.
                    // I think I've dialed it in okay but adjust the values as necessary.
                    thresholdDelta: 20,
                    thresholdTime: 75,
                }}
                slidesPerView="auto"
                spaceBetween="1px"
                cssMode={cssMode}
                freeMode={freeMode}
            >
                {items.map((item, index) => {
                    if (item.productVideo) {
                        return item.productVideo.map((video, videoIndex) => (
                            <SwiperSlide key={`${index}-${videoIndex}`}>
                                <VideoSlideContent
                                    video={video}
                                    poster={item.poster}
                                    aspectRatio={videoAspectRatio}
                                />
                            </SwiperSlide>
                        ));
                    } else if (item.productImage) {
                        return item.productImage.map((image, imageIndex) => (
                            <SwiperSlide key={`${index}-${imageIndex}`}>
                                <StaticImage
                                    desktopImage={replaceShopifyCdnUrl(
                                        image.desktopImage,
                                        1077,
                                        1436
                                    )}
                                    mobileImage={replaceShopifyCdnUrl(
                                        image.mobileImage,
                                        615,
                                        800
                                    )}
                                    fullWidth={true}
                                    altDescription={
                                        image.altText || fallbackImageAltText
                                    }
                                />
                            </SwiperSlide>
                        ));
                    }
                })}
            </Swiper>
        </>
    );
};

const SwiperPDPGalleryVariant = ({
    block,
    fallbackImageAltText,
}: SwiperProps) => {
    const { items } = block;
    const { width, height } = items[0]?.productImage?.[0] || {};

    const firstItemAspectRatio =
        width && height ? `${width}/${height}` : '4 / 5';

    return (
        <>
            {/* Mobile Variant */}
            <div
                className={cn(s.PDPGalleryMobile, 'hidden-on-desktop')}
                style={{ aspectRatio: firstItemAspectRatio }}
            >
                <RenderSwiper
                    direction="horizontal"
                    items={items}
                    // enable css mode to prevent known library bug
                    // https://github.com/nolimits4web/swiper/issues/5743
                    cssMode={true}
                    fallbackImageAltText={fallbackImageAltText}
                    videoAspectRatio={firstItemAspectRatio}
                />
            </div>

            {/* Desktop Variant */}
            <div
                className={cn(s.PDPGalleryDesktop, 'hidden-on-mobile')}
                style={{ aspectRatio: firstItemAspectRatio }}
            >
                <RenderSwiper
                    direction="vertical"
                    items={items}
                    // cssMode uses native scroll (no transform layer) so the
                    // overlaid title's mix-blend-mode resolves in WebKit.
                    cssMode={true}
                    freeMode={false}
                    fallbackImageAltText={fallbackImageAltText}
                    videoAspectRatio={firstItemAspectRatio}
                />
            </div>
        </>
    );
};

export default SwiperPDPGalleryVariant;
